/**
 * Банк паттернов FSI: каждая единица — рамка (frame) со слотом и 3–4
 * подстановки, которые тренируют ровно ту же конструкцию.
 */

import { z } from 'zod';

export const PATTERN_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1'] as const;
export const PatternLevelSchema = z.enum(PATTERN_LEVELS);
export type PatternLevel = z.infer<typeof PatternLevelSchema>;

/** Паттернов на ступень: 5 × 300 = 1500. */
export const PATTERNS_PER_LEVEL = 300;

export const PatternVariantSchema = z.object({
  /** Рамка + заполненный слот: «I like coffee». */
  sentence: z.string().trim().min(2).max(160),
  translation: z.string().trim().min(2).max(200),
});
export type PatternVariant = z.infer<typeof PatternVariantSchema>;

export const PatternSchema = z
  .object({
    patternId: z.string().regex(/^(A1|A2|B1|B2|C1)-P\d{3}$/),
    level: PatternLevelSchema,
    /** Неизменная часть: «I like», «It depends on». */
    frame: z.string().trim().min(2).max(80),
    /** Подпись слота: «[object]», «[verb]». */
    slot: z
      .string()
      .trim()
      .transform((slot) => (slot.startsWith('[') ? slot : `[${slot}]`).toLowerCase())
      .pipe(z.string().regex(/^\[[a-z /'-]{2,40}\]$/)),
    translation: z.string().trim().min(2).max(120),
    /** Образ рамки для генератора картинок — суть конструкции, не варианта. */
    imagePrompt: z.string().trim().min(20).max(400),
    variants: z.array(PatternVariantSchema).min(3).max(4),
  })
  .superRefine((pattern, ctx) => {
    const frame = pattern.frame.toLowerCase();
    for (const [index, variant] of pattern.variants.entries()) {
      if (!variant.sentence.toLowerCase().includes(frame)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['variants', index, 'sentence'], message: `вариант не содержит рамку «${pattern.frame}»` });
      }
    }
    const unique = new Set(pattern.variants.map((variant) => variant.sentence.toLowerCase()));
    if (unique.size !== pattern.variants.length) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['variants'], message: 'варианты повторяются' });
  });
export type Pattern = z.infer<typeof PatternSchema>;

export const PatternBankSchema = z.object({ version: z.literal(1), patterns: z.array(PatternSchema) });
export type PatternBank = z.infer<typeof PatternBankSchema>;
